import { useMemo } from "react";
import { Plus } from "lucide-react";
import type { TimeSeriesEntry, ColumnDef } from "./types";
import TimeSeriesInputTable from "./TimeSeriesInputTable";

// ---------- types ----------

interface CapitalDetailPanelProps {
  entries: TimeSeriesEntry[];
  onEntryChange: (entryId: string, key: string, value: unknown) => void;
  onEntryAdd: (template: Record<string, unknown>) => void;
  onEntryDelete: (entryId: string) => void;
  onSave: () => Promise<void>;
  onReset: () => void;
  isDirty: boolean;
  isSaving: boolean;
}

// ---------- column definitions ----------

const UNITS_OPTIONS = [
  { value: "$ Total", label: "$ Total" },
  { value: "$/SF", label: "$/SF" },
];

const RECURRENCE_OPTIONS = [
  { value: "One-Time", label: "One-Time" },
  { value: "Annual", label: "Annual" },
  { value: "Every 2 Years", label: "Every 2 Years" },
  { value: "Every 3 Years", label: "Every 3 Years" },
  { value: "Every 5 Years", label: "Every 5 Years" },
  { value: "Every 10 Years", label: "Every 10 Years" },
];

const COLUMNS: ColumnDef[] = [
  { key: "start_date", header: "Date", type: "date", width: "130px" },
  { key: "amount", header: "Amount", type: "currency", width: "120px" },
  {
    key: "units",
    header: "Units",
    type: "dropdown",
    options: UNITS_OPTIONS,
    width: "100px",
  },
  { key: "description", header: "Description", type: "text", width: "200px" },
  {
    key: "recurrence",
    header: "Recurrence",
    type: "dropdown",
    options: RECURRENCE_OPTIONS,
    width: "140px",
  },
  { key: "inflation_adj", header: "Inflation Adj", type: "checkbox", width: "90px" },
];

// ---------- helpers ----------

function entriesToRows(entries: TimeSeriesEntry[]) {
  return entries.map((entry) => ({
    id: entry.entry_id,
    source: entry.source,
    values: {
      start_date: entry.start_date,
      amount: entry.amount,
      units: entry.units,
      description: entry.extra?.description ?? "",
      recurrence: entry.extra?.recurrence ?? "One-Time",
      inflation_adj: entry.extra?.inflation_adj ?? false,
    },
  }));
}

// ---------- component ----------

export default function CapitalDetailPanel({
  entries,
  onEntryChange,
  onEntryAdd,
  onEntryDelete,
  onSave,
  onReset,
  isDirty,
  isSaving,
}: CapitalDetailPanelProps) {
  const rows = useMemo(() => entriesToRows(entries), [entries]);

  const actionButtons = useMemo(
    () => [
      {
        label: "+ Add Item",
        icon: <Plus className="w-3.5 h-3.5" />,
        rowTemplate: {
          start_date: new Date().toISOString().slice(0, 10),
          amount: 0,
          units: "$ Total",
          description: "",
          recurrence: "One-Time",
          inflation_adj: false,
        },
        variant: "primary" as const,
      },
    ],
    [],
  );

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="bg-white border border-grove-300 rounded-xl p-4">
        <h3 className="font-display text-sm font-semibold text-bark-800 mb-1">
          Capital Expenditures
        </h3>
        <p className="text-xs text-bark-400">
          Define one-time and recurring capital expenditures. Amounts are deducted below NOI
          in the cash flow waterfall.
        </p>
      </div>

      {/* Input table */}
      <TimeSeriesInputTable
        columns={COLUMNS}
        rows={rows}
        onRowChange={onEntryChange}
        onRowAdd={onEntryAdd}
        onRowDelete={onEntryDelete}
        onReset={onReset}
        actionButtons={actionButtons}
        isDirty={isDirty}
        onSave={onSave}
        isSaving={isSaving}
      />
    </div>
  );
}
